// src/api/tokenApi.js

import axios from 'axios';
import apiClient from './apiClient';

const tokenClient = apiClient('');

const tokenApi = {
    /**
     * Получить токены с пагинацией.
     * @param {number} page — номер страницы, по умолчанию 1
     * @param {number} limit — сколько элементов на страницу, по умолчанию 20
     * @returns {Promise<{ items: any[], total: number, page: number, limit: number }>}
     */
    async getTokens(page = 1, limit = 20) {
        try {
            const response = await tokenClient.get('/token', {
                params: { page, limit },
            });
            return response.data; // { items, total, page, limit }
        } catch (error) {
            console.error('Ошибка при получении токенов (с пагинацией):', error);
            throw error;
        }
    },

    /**
     * Получить все токены без пагинации (если реализован эндпоинт /token/all).
     * Лучше использовать getTokens, если токенов много.
     */
    async getAllTokens() {
        try {
            const response = await tokenClient.get('/token/all');
            return response.data;
        } catch (error) {
            console.error('Ошибка при получении всех токенов:', error);
            throw error;
        }
    },

    /**
     * Получить один токен по его адресу из БД.
     * @param {string} address — адрес токена
     * @returns {Promise<any>}
     */
    async getTokenByAddress(address) {
        try {
            const response = await tokenClient.get(`/token/address/${address}`);
            return response.data;
        } catch (error) {
            console.error(`Ошибка при получении токена по адресу ${address}:`, error);
            throw error;
        }
    },

    /**
     * Получить токены по заданному блокчейну с пагинацией.
     * @param {string} chain — название блокчейна, например 'ethereum'
     * @param {number} page — номер страницы, по умолчанию 1
     * @param {number} limit — сколько элементов на страницу, по умолчанию 20
     * @returns {Promise<{ items: any[], total: number, page: number, limit: number }>}
     */
    async getTokensByBlockchain(chain, page = 1, limit = 20) {
        try {
            const response = await tokenClient.get(`/token/blockchain/${chain}`, {
                params: { page, limit },
            });
            return response.data;
        } catch (error) {
            console.error(`Ошибка при получении токенов блокчейна ${chain}:`, error);
            throw error;
        }
    },

    /**
     * Поиск токена во внешних API (TrustWallet, CoinGecko, Moralis и т.д.).
     * @param {string} address — адрес токена
     * @param {string} blockchain — блокчейн (ethereum, bsc и т.д.)
     */
    async lookupToken(address, blockchain) {
        try {
            const response = await tokenClient.get('/token/lookup', {
                params: { address, blockchain },
            });
            return response.data;
        } catch (error) {
            console.error('Ошибка при поиске токена:', error);
            throw error;
        }
    },

    /**
     * Ручное создание токена (multipart/form-data).
     * @param {FormData} formData — поля name, symbol, address, blockchain, файл file
     */
    async createManualToken(formData) {
        try {
            const response = await axios.post(
                '/token/manual',
                formData,
                {
                    baseURL: tokenClient.defaults.baseURL,
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (error) {
            console.error('Ошибка при создании токена вручную:', error);
            throw error;
        }
    },

    /**
     * Обновить токен по ID.
     * @param {number} id — ID токена
     * @param {Object} updates — поля для обновления
     */
    async updateToken(id, updates) {
        try {
            const response = await tokenClient.patch(`/token/${id}`, updates);
            return response.data;
        } catch (error) {
            console.error(`Ошибка при обновлении токена с ID ${id}:`, error);
            throw error;
        }
    },

    /**
     * Удалить токен по ID.
     * @param {number} id — ID токена
     */
    async deleteToken(id) {
        try {
            const response = await tokenClient.delete(`/token/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Ошибка при удалении токена с ID ${id}:`, error);
            throw error;
        }
    },
};

export default tokenApi;
